import { useState } from 'react'
import { SearchState } from '../state'
import { SearchStateManager } from '../types'

/**
 * Marshals reads/writes to React state only (no URL search params). Search resets when the
 * component unmounts. Used internally by {@link useSearchState} when `shouldPersist` is false.
 *
 * Each {@link SearchState} value is a `string[]` (see `firstValue` and helpers in `./state`).
 * `updateSearchState` shallow-merges into the previous object.
 *
 * @param defaultState Initial search state (e.g. default filters, page, pageSize as string arrays).
 * @returns `[searchState, updateSearchState]` — same tuple shape as {@link SearchStateManager}.
 *
 * @example Typical usage — transient list search via `useSearch` (third argument `false` uses this hook inside {@link useSearchState}):
 * ```tsx
 * import { useSearch } from '@shared/search/useSearch'
 * import { fetchMySpaces, searchDefaults } from './filters/utils'
 *
 * const { results, setFilters, isLoading } = useSearch(fetchMySpaces, searchDefaults, false)
 * ```
 *
 * @example Direct usage — custom UI that is not `useSearch` (same merge semantics as URL-backed search):
 * ```tsx
 * const [searchState, updateSearchState] = useSearchStateLocal({
 *   status: ['active'],
 *   page: ['1'],
 * })
 * // merge: keeps `status`, replaces `page`
 * updateSearchState({ page: ['2'] })
 * ```
 */
export const useSearchStateLocal = (defaultState: SearchState): SearchStateManager => {
  const [searchState, setSearchState] = useState<SearchState>(defaultState)

  const updateSearchState = (updatedSearchState: SearchState) => {
    setSearchState((previousSearchState: SearchState) => {
      return {
        ...previousSearchState,
        ...updatedSearchState,
      }
    })
  }

  return [searchState, updateSearchState]
}

export default useSearchStateLocal
